import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert,
  CircularProgress,
  useTheme,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Reply as ReplyIcon,
  Schedule as ScheduleIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Done as DoneIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { collection, query, where, orderBy, getDocs, or, doc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';

const MotionCard = motion(Card);

interface ContactMessage {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  timestamp: any;
  status: 'read' | 'unread' | 'replied' | 'completed';
  response?: string;
  respondedAt?: any;
  respondedBy?: string;
  userId?: string;
  userReply?: string;
  userRepliedAt?: any;
}

interface MessageCenterProps {
  refreshTrigger?: number;
}

export const MessageCenter: React.FC<MessageCenterProps> = ({ refreshTrigger }) => {
  const { currentUser } = useAuth();
  const theme = useTheme();
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [replyDialog, setReplyDialog] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const loadUserMessages = useCallback(async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Query messages by user ID (for authenticated submissions) or email (for older messages)
      const messagesQuery = query(
        collection(db, 'contact_messages'),
        or(
          where('userId', '==', currentUser.uid),
          where('email', '==', currentUser.email)
        ),
        orderBy('timestamp', 'desc')
      );
      
      const messagesSnapshot = await getDocs(messagesQuery);
      const userMessages: ContactMessage[] = [];

      messagesSnapshot.forEach((doc) => {
        const messageData = doc.data();
        userMessages.push({
          id: doc.id,
          ...messageData,
        } as ContactMessage);
      });

      setMessages(userMessages);
    } catch (error) {
      console.error('Error loading user messages:', error);
      setError('Failed to load your messages');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadUserMessages();
  }, [currentUser, loadUserMessages]);

  // Reload messages when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0 && currentUser) {
      loadUserMessages();
    }
  }, [refreshTrigger, currentUser, loadUserMessages]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'replied':
        return 'info';
      case 'read':
        return 'warning';
      case 'unread':
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <DoneIcon fontSize="small" />;
      case 'replied':
        return <ReplyIcon fontSize="small" />;
      case 'read':
        return <CheckCircleIcon fontSize="small" />;
      case 'unread':
      default:
        return <ScheduleIcon fontSize="small" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'replied':
        return 'Replied';
      case 'read':
        return 'Read';
      case 'unread':
      default:
        return 'Pending';
    }
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return 'Unknown date';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return format(date, 'MMM dd, yyyy \'at\' h:mm a');
    } catch (error) {
      return 'Invalid date';
    }
  };

  const handleMarkComplete = async (messageId: string) => {
    if (!currentUser) return;

    try {
      setActionLoading(messageId);
      await updateDoc(doc(db, 'contact_messages', messageId), {
        status: 'completed',
        completedAt: serverTimestamp(),
        completedBy: currentUser.uid
      });

      // Update local state
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, status: 'completed' as const }
          : msg
      ));

      setSnackbar({
        open: true,
        message: 'Message marked as completed',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error marking message as complete:', error);
      setSnackbar({
        open: true,
        message: 'Failed to mark message as complete',
        severity: 'error'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleOpenReply = (message: ContactMessage) => {
    setSelectedMessage(message);
    setReplyText('');
    setReplyDialog(true);
  };

  const handleSendReply = async () => {
    if (!currentUser || !selectedMessage || !replyText.trim()) return;

    try {
      setReplyLoading(true);
      
      // Update the original message with user reply
      await updateDoc(doc(db, 'contact_messages', selectedMessage.id), {
        userReply: replyText.trim(),
        userRepliedAt: serverTimestamp(),
        status: 'completed' // Mark as completed when user replies
      });

      // Create a new message as a follow-up (optional, for admin tracking)
      await addDoc(collection(db, 'contact_messages'), {
        name: currentUser.displayName || 'Unknown',
        email: currentUser.email,
        subject: `Re: ${selectedMessage.subject}`,
        message: replyText.trim(),
        timestamp: serverTimestamp(),
        status: 'unread',
        userId: currentUser.uid,
        originalMessageId: selectedMessage.id,
        isReply: true
      });

      // Update local state
      setMessages(prev => prev.map(msg => 
        msg.id === selectedMessage.id 
          ? { 
              ...msg, 
              userReply: replyText.trim(),
              userRepliedAt: new Date(),
              status: 'completed' as const
            }
          : msg
      ));

      setSnackbar({
        open: true,
        message: 'Reply sent successfully',
        severity: 'success'
      });

      setReplyDialog(false);
      setSelectedMessage(null);
      setReplyText('');
    } catch (error) {
      console.error('Error sending reply:', error);
      setSnackbar({
        open: true,
        message: 'Failed to send reply',
        severity: 'error'
      });
    } finally {
      setReplyLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (messages.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        You haven't sent any messages yet. Use the form below to get in touch with our team.
      </Alert>
    );
  }



  return (
    <>
      <MotionCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        sx={{
          mb: 4,
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 215, 0, 0.2)',
        }}
      >
        <CardContent>
          <Box display="flex" alignItems="center" mb={3}>
            <EmailIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography
              variant="h5"
              component="h2"
              sx={{ 
                color: 'primary.main',
                fontWeight: 700,
              }}
            >
              Your Messages
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Track the status of your messages and view any responses from our team.
          </Typography>

          <List disablePadding>
            {messages.map((message, index) => (
              <Box key={message.id}>
                <Accordion
                  sx={{
                    background: 'transparent',
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': {
                      margin: 0,
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      px: 0,
                      '& .MuiAccordionSummary-content': {
                        alignItems: 'center',
                      },
                    }}
                  >
                    <Box flex={1} display="flex" alignItems="center" justifyContent="space-between">
                      <Box flex={1}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {message.subject}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatTimestamp(message.timestamp)}
                        </Typography>
                      </Box>
                      <Chip
                        icon={getStatusIcon(message.status)}
                        label={getStatusLabel(message.status)}
                        color={getStatusColor(message.status)}
                        size="small"
                        sx={{ ml: 2, pointerEvents: 'none' }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0, pt: 0 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        <strong>Your Message:</strong>
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 3, p: 2, bgcolor: 'rgba(0,0,0,0.1)', borderRadius: 1 }}>
                        {message.message}
                      </Typography>

                      {message.status === 'replied' && message.response && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            <strong>Our Response:</strong>
                            {message.respondedAt && (
                              <span style={{ marginLeft: 8 }}>
                                • {formatTimestamp(message.respondedAt)}
                              </span>
                            )}
                          </Typography>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              p: 2, 
                              bgcolor: 'rgba(25, 118, 210, 0.1)', 
                              borderRadius: 1,
                              borderLeft: `4px solid ${theme.palette.primary.main}`,
                            }}
                          >
                            {message.response}
                          </Typography>
                        </Box>
                      )}

                      {message.userReply && (
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            <strong>Your Reply:</strong>
                            {message.userRepliedAt && (
                              <span style={{ marginLeft: 8 }}>
                                • {formatTimestamp(message.userRepliedAt)}
                              </span>
                            )}
                          </Typography>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              p: 2, 
                              bgcolor: 'rgba(0, 0, 0, 0.1)', 
                              borderRadius: 1,
                              borderLeft: `4px solid ${theme.palette.secondary.main}`,
                            }}
                          >
                            {message.userReply}
                          </Typography>
                        </Box>
                      )}

                      {/* Action Buttons */}
                      {message.status !== 'completed' && (
                        <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          {message.status === 'replied' && message.response && !message.userReply && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<ReplyIcon />}
                              onClick={() => handleOpenReply(message)}
                              sx={{ color: 'primary.main', borderColor: 'primary.main' }}
                            >
                              Reply
                            </Button>
                          )}
                          
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DoneIcon />}
                            onClick={() => handleMarkComplete(message.id)}
                            disabled={actionLoading === message.id}
                            sx={{ color: 'success.main', borderColor: 'success.main' }}
                          >
                            {actionLoading === message.id ? 'Marking...' : 'Mark Complete'}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
                {index < messages.length - 1 && <Divider sx={{ my: 1 }} />}
              </Box>
            ))}
          </List>
        </CardContent>
      </MotionCard>

      {/* Reply Dialog */}
      <Dialog 
        open={replyDialog} 
        onClose={() => setReplyDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Reply to: {selectedMessage?.subject}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Original admin response:
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              p: 2, 
              mb: 3,
              bgcolor: 'rgba(25, 118, 210, 0.1)', 
              borderRadius: 1,
              borderLeft: `4px solid ${theme.palette.primary.main}`,
            }}
          >
            {selectedMessage?.response}
          </Typography>
          
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Your Reply"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Type your response here..."
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setReplyDialog(false)}
            startIcon={<CloseIcon />}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSendReply}
            variant="contained"
            startIcon={<SendIcon />}
            disabled={!replyText.trim() || replyLoading}
          >
            {replyLoading ? 'Sending...' : 'Send Reply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}; 